import { Capacitor, CapacitorHttp } from '@capacitor/core';

/**
 * Persistent avatar cache.
 *
 * Firebase Storage serves avatars with `cache-control: private, max-age=0` and no CORS headers, so
 * the WebView re-checks every image on every view and a flaky connection leaves half-drawn or
 * broken avatars. Instead we download each avatar once, keep the bytes in IndexedDB and hand out
 * a local object URL from then on:
 *  - native (Capacitor): CapacitorHttp downloads natively, so CORS doesn't apply
 *  - web: a CORS fetch, which works once the Storage bucket allows the app's origin
 *  - otherwise the remote URL is used as before
 * Avatar URLs carry a download token that changes when a photo is replaced, so the URL is a safe
 * cache key.
 */

const DB_NAME = 'padel-mvp-avatars';
const STORE = 'images';
const MAX_CONCURRENT = 3;

const objectUrls = new Map(); // remote URL -> blob: URL
const pending = new Set();
const listeners = new Set();
let notifyTimer = null;
let corsBlocked = false;
let dbPromise = null;

const isRemote = (url) => typeof url === 'string' && /^https?:\/\//.test(url);

const notify = () => {
  clearTimeout(notifyTimer);
  notifyTimer = setTimeout(() => listeners.forEach((fn) => fn()), 150);
};

/** Re-render hook: called (debounced) whenever newly cached avatars become available. */
export function subscribeAvatarCache(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Local copy of an avatar if we have one, otherwise the original URL. */
export function getResolvedAvatar(url) {
  return objectUrls.get(url) || url;
}

const openDb = () => {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'url' });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch (_) {
      resolve(null);
    }
  });
  return dbPromise;
};

const idbRequest = (mode, run) =>
  openDb().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) return resolve(null);
        try {
          const req = run(db.transaction(STORE, mode).objectStore(STORE));
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => resolve(null);
        } catch (_) {
          resolve(null);
        }
      })
  );

/** Load every saved avatar into memory as object URLs. Call once at startup. */
export async function hydrateAvatarCache() {
  const entries = (await idbRequest('readonly', (store) => store.getAll())) || [];
  let added = 0;
  entries.forEach(({ url, blob }) => {
    if (!objectUrls.has(url) && blob && blob.size > 0) {
      objectUrls.set(url, URL.createObjectURL(blob));
      added += 1;
    }
  });
  if (added) notify();
}

const base64ToBlob = (base64, type) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type });
};

async function download(url) {
  if (Capacitor.isNativePlatform?.()) {
    try {
      const res = await CapacitorHttp.get({ url, responseType: 'blob' });
      const type = res.headers?.['Content-Type'] || res.headers?.['content-type'] || 'image/jpeg';
      if (res.status === 200 && typeof res.data === 'string' && type.startsWith('image/')) {
        return base64ToBlob(res.data, type);
      }
    } catch (_) {
      /* fall through to remote URL */
    }
    return null;
  }

  if (corsBlocked) return null;
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return blob.type.startsWith('image/') ? blob : null;
  } catch (_) {
    corsBlocked = true; // bucket has no CORS for this origin; stop trying this session
    return null;
  }
}

/** Download and persist any avatars we don't have yet (a few at a time). */
export function cacheAvatars(urls) {
  const queue = [...new Set((urls || []).filter(isRemote))].filter(
    (url) => !objectUrls.has(url) && !pending.has(url)
  );
  queue.forEach((url) => pending.add(url));

  const worker = async () => {
    while (queue.length) {
      const url = queue.shift();
      const blob = await download(url);
      pending.delete(url);
      if (!blob || blob.size === 0) continue;
      objectUrls.set(url, URL.createObjectURL(blob));
      idbRequest('readwrite', (store) => store.put({ url, blob, savedAt: Date.now() }));
      notify();
    }
  };
  for (let i = 0; i < Math.min(MAX_CONCURRENT, queue.length); i += 1) worker();
}

/* ---------- Local initials avatar (never needs the network) ---------- */

const PALETTE = ['#2569e8', '#7631d6', '#df582b', '#31b3d6', '#c88921', '#1f9d72', '#d6336c', '#4793cf'];

export function getInitialsAvatar(name) {
  const clean = String(name || 'Player').trim();
  const initials =
    clean
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0].toUpperCase())
      .join('') || 'P';
  let hash = 0;
  for (let i = 0; i < clean.length; i += 1) hash = (hash * 31 + clean.charCodeAt(i)) >>> 0;
  const bg = PALETTE[hash % PALETTE.length];
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96">` +
    `<rect width="96" height="96" fill="${bg}"/>` +
    `<text x="48" y="50" dominant-baseline="middle" text-anchor="middle" fill="#fff" ` +
    `font-family="Nunito Sans, Helvetica, Arial, sans-serif" font-size="38" font-weight="800">${initials}</text>` +
    `</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/* ---------- App-wide safety net for avatars that fail or arrive empty ---------- */

const looksLikeAvatar = (img) =>
  /avatar/i.test(img.className) ||
  /avatar/i.test(img.alt || '') ||
  /firebasestorage\.googleapis\.com|ui-avatars\.com/.test(img.currentSrc || img.src || '');

const nameFromAlt = (alt) => String(alt || '').replace(/'s avatar$/i, '').replace(/ logo$/i, '');

const recover = (img) => {
  const tries = Number(img.dataset.avatarRecover || 0);
  img.dataset.avatarRecover = String(tries + 1);
  const src = img.getAttribute('src') || '';
  if (tries === 0 && isRemote(src)) {
    img.src = `${src}${src.includes('?') ? '&' : '?'}_r=${Date.now()}`; // one cache-busting retry
  } else if (!src.startsWith('data:')) {
    img.src = getInitialsAvatar(nameFromAlt(img.alt));
  }
};

/**
 * Retries a failed avatar once, then swaps in local initials. Also catches images that "load"
 * with no pixels (truncated downloads on poor connections), which otherwise render as broken.
 */
export function installAvatarFallback() {
  if (typeof document === 'undefined' || document.__avatarFallbackInstalled) return;
  document.__avatarFallbackInstalled = true;
  document.addEventListener(
    'error',
    (e) => {
      const img = e.target;
      if (img instanceof HTMLImageElement && looksLikeAvatar(img)) recover(img);
    },
    true
  );
  document.addEventListener(
    'load',
    (e) => {
      const img = e.target;
      if (img instanceof HTMLImageElement && img.naturalWidth === 0 && looksLikeAvatar(img)) recover(img);
    },
    true
  );
}
